import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SharedService } from '@shared/shared.service';

export type AssetIntelligenceTab =
  | 'device'
  | 'sim'
  | 'compatibility'
  | 'digitalTwin'
  | 'remoteOps';

export interface DeviceNavigationTarget {
  deviceId?: string;
  serialNumber?: string;
  assetName?: string;
  groupId?: string;
}

@Injectable({ providedIn: 'root' })
export class DeviceNavigationService {
  private readonly router = inject(Router);
  private readonly sharedService = inject(SharedService);

  goToAssetIntelligence(
    target: DeviceNavigationTarget,
    tab?: AssetIntelligenceTab,
  ): void {
    this.resolveDevice(target).subscribe((device) => {
      if (!device?.deviceId) {
        this.notifyDeviceNotFound(target);
        return;
      }
      const queryParams: Record<string, string> = {};
      if (device.groupId) {
        queryParams.groupId = String(device.groupId);
      }
      if (tab) {
        queryParams.tab = tab;
      }
      this.router.navigate(['/devices', device.deviceId], {
        queryParams: Object.keys(queryParams).length ? queryParams : undefined,
      });
    });
  }

  goToTripDashboard(target: DeviceNavigationTarget): void {
    this.resolveDevice(target).subscribe((device) => {
      if (!device?.deviceId) {
        this.notifyDeviceNotFound(target);
        return;
      }
      this.sharedService.setdefaultselectvehicle(device);
      const queryParams: Record<string, string> = {
        deviceId: String(device.deviceId),
      };
      if (device.groupId) {
        queryParams.groupId = String(device.groupId);
      }
      this.router.navigate(['/trips'], { queryParams });
    });
  }

  goToLive(target: DeviceNavigationTarget): void {
    this.resolveDevice(target).subscribe((device) => {
      if (!device?.deviceId && !device?.serialNumber) {
        this.notifyDeviceNotFound(target);
        return;
      }
      const queryParams: Record<string, string> = {};
      if (device.deviceId) {
        queryParams.deviceId = String(device.deviceId);
      }
      if (device.serialNumber) {
        queryParams.serialNumber = device.serialNumber;
      }
      if (device.assetName) {
        queryParams.assetName = device.assetName;
      }
      if (device.groupId) {
        queryParams.groupId = String(device.groupId);
      }
      this.router.navigate(['/live'], { queryParams });
    });
  }

  private resolveDevice(
    target: DeviceNavigationTarget,
  ): Observable<DeviceNavigationTarget | null> {
    if (!target) {
      return of(null);
    }
    const serialNumber = target.serialNumber;
    const deviceId = target.deviceId;
    if (deviceId && target.groupId) {
      return of({ ...target });
    }
    if (!serialNumber && !deviceId) {
      return of(null);
    }

    const key = serialNumber ? 'serialNumber' : 'deviceId';
    const value = serialNumber || deviceId;
    return this.sharedService.onSearchVehicle(key, value).pipe(
      map((res) => {
        const list = Array.isArray(res) ? res : [];
        const match = list.find((device) =>
          serialNumber
            ? this.isSameSerialNumber(device?.serialNumber, serialNumber)
            : this.isSameId(device?.deviceId, deviceId),
        );
        if (!match) {
          return deviceId ? { ...target } : null;
        }
        const resolved: DeviceNavigationTarget = {
          deviceId: match.deviceId ?? target.deviceId,
          serialNumber: match.serialNumber ?? serialNumber,
          assetName: match.assetName ?? target.assetName,
          groupId: match.groupId ?? target.groupId,
        };
        return resolved;
      }),
      catchError((error) => {
        this.sharedService.getErrorMsg(error);
        return of(null);
      }),
    );
  }

  private isSameId(value: unknown, other?: string): boolean {
    return String(value ?? '') === String(other ?? '') && value != null && other != null;
  }

  private isSameSerialNumber(value: unknown, serialNumber: string): boolean {
    return (
      String(value ?? '').toLowerCase() === String(serialNumber).toLowerCase()
    );
  }

  private notifyDeviceNotFound(target: DeviceNavigationTarget): void {
    const serialNumber = target?.serialNumber;
    this.sharedService.setResponseMessage({
      success: false,
      message: serialNumber
        ? `Device ${serialNumber} is no longer available.`
        : 'Device is no longer available.',
    });
  }
}
